import { Component, ElementRef, EventEmitter, Input, Output, Renderer2 } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import {
  Inverter,
  isNotNullOrUndefined,
  NodeType,
  ProductModelService,
  System,
} from 'ngx-core-services';
import { format } from 'date-fns';
import { ECharts, EChartsOption } from 'echarts';
import * as echarts from 'echarts/core';
import { isEqual } from 'lodash';

import { distinctUntilChanged, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import {
  ChartAttributes,
  DeviceAttributes,
  DeviceCategoryId,
  adaptPowersToGraph,
  appBuildGenericDailyGraph,
  attributeLabels,
  categoryLabels,
  chartDeviceAttributes,
  deviceAttributes,
  fromPowerDetailsSeriesToPowerSeries,
  updateSelectedAndAxisLimits,
} from '../utils';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import {
  SystemsState,
  requestSystemDeviceDailyPowers,
  selectedSystemMetadataNodesType,
  selectedSystemTz,
  selectedSystemUid,
} from '../../store';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { selectDevicePowersBySelectedSystem, selectDevicePowersStateBySelectedSystem } from '../../store/selectors/device-daily-powers.selectors';
import { exportToExcel } from '../utils/export-xlsx';

const getLabels = () => ({
  ...attributeLabels,
  ...categoryLabels,
  clear: 'Clear',
  createChart: 'Create chart',
  deleteChart: 'Delete chart',
  youCanAddPropertiesForUpToTwoUnitsOfMeasurement: 'You can add properties for up to two units of measurement',
  noDataForTheSelectedPeriod: 'No data for the selected period',
  loading: 'Loading ...',
  title: 'Device daily powers',
  updateChart: 'Update chart',
  compareGraphs: 'Compare',
});

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-750': 750,
})
@Component({
  selector: 'lib-device-daily-powers',
  templateUrl: './device-daily-powers.component.html',
  styleUrls: ['./device-daily-powers.component.scss'],
})
export class DeviceDailyPowersComponent implements ICustomBlock, IReduxBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  @Input() showTitle = true;
  @Input() compareGraphs?: 'add' | 'close';

  readonly _deviceId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set deviceUid(value: string | undefined) {
    this.chartInstances = [];
    this.chartsAttributes$.next([]);
    this.cachedDeviceConfig$.next(undefined);
    this.dataZoomState = null;

    this._deviceId$.next(value);
  }

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }

  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  @Output() onShowCompareGraphEvent = new EventEmitter<'add' | 'close'>();

  chartInstances: ECharts[] = [];
  chartsAttributes$ = new BehaviorSubject<ChartAttributes[][]>([]);
  zoomed = false;
  private dataZoomState: { start: number; end: number } | null = null;
  private cachedDeviceConfig$ = new BehaviorSubject<{n_mppt: number, n_strings: number} | undefined>(undefined);

  _currentWidth = 0;
  set currentWidth(value: number) {
    if (value === this._currentWidth) {
      return;
    }

    this._currentWidth = value;
    this.chartInstances.forEach((chart) => chart.resize());
  }
  get currentWidth() {
    return this._currentWidth;
  }

  @Input() extraSourceOptions: NodeType[] = [];

  readonly sourceSelection$ = new BehaviorSubject<NodeType[]>(['solar', 'grid', 'home', 'battery']);
  readonly dateSelection$ = new BehaviorSubject<string>(format(new Date(), 'yyyy-MM-dd'));
  readonly selectedSystemNodes$ = this.store.select(selectedSystemMetadataNodesType).pipe(isNotNullOrUndefined());
  readonly systemTz$ = this.store.select(selectedSystemTz).pipe(isNotNullOrUndefined(),distinctUntilChanged());

  readonly sourceOptions$ = combineLatest([
    this.selectedSystemNodes$,
    this._deviceId$
  ]).pipe(
    switchMap(async ([systemSourceOption, deviceId]) => {
      let nativeSources: NodeType[];
      if( deviceId ) {
        const product = new ProductModelService(deviceId);
        if (product.type?.name === 'meter' || product.type?.name === 'enerclic') {
          nativeSources = ['grid'];
        } else if (product.type?.name !== 'inverter') {
          nativeSources = [];
        } else if(product.type.value === 'pv') {
          nativeSources = ['solar'];
        } else {
          nativeSources = product.type.value === 'storage'
            ? ['battery']
            : ['solar', 'battery'];
        }
      } else {
        nativeSources = systemSourceOption;
      }
      return nativeSources;
    }),
    map((nativeSources) => {
      const extras = this.extraSourceOptions.filter(s => !nativeSources.includes(s));
      return [...nativeSources, ...extras];
    }),
    tap((allSources) => {
      if (allSources.length) {
        this.sourceSelection$.next(allSources);
      }
    }),
  );

  readonly requestPowerData$ = combineLatest([
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this.dateSelection$,
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(([systemId, deviceId, date, forceRefresh, instanceId]) => {
      const range = {
        from: date,
        to: date,
      }
      this.store.dispatch(requestSystemDeviceDailyPowers({ systemId, instanceId, deviceId, range, forceRefresh }));
    }),
  );

  readonly isLoading$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectDevicePowersStateBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
    isNotNullOrUndefined(),
    map((state) => state === 'pending'),
  );

  readonly powerData$ = combineLatest([
    this.systemTz$,
    this._instanceId$.pipe(
      switchMap((instanceId) => this.store.select(selectDevicePowersBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
    ),
    this.sourceSelection$,
    this.sourceOptions$,
    this.dateSelection$,
  ]).pipe(
    map(([tz, powers, selectedSource, sourceOptions, date]) => {
      const echartsOption = this.buildPowerGraph(`${tz}`, selectedSource, sourceOptions, date, powers);
      return {
        powers,
        echartsOption,
      }
    }),
  );

  readonly chartsOpts$ = combineLatest([
    this.systemTz$,
    this.powerData$.pipe(
      distinctUntilChanged((a, b) => isEqual(a.powers, b.powers)),
    ),
    this.chartsAttributes$,
    this.dateSelection$.pipe(
      distinctUntilChanged(),
    ),
  ]).pipe(
    map(([tz, powerData, chartsAttributes, date]) => {
      if (chartsAttributes.length === 0) {
        return [];
      }

      const { powers } = powerData;
      const fullSeries = fromPowerDetailsSeriesToPowerSeries(powers, date, `${tz}`);

      const validCharts = [];
      for (const attributes of chartsAttributes) {
        // Filter out undefined series and validate attributes
        const series = attributes
          .map(({ id }) => fullSeries[id])
          .filter(s => s && s.name); // Ensure series exists and has a name

        // Only create chart if we have valid series and attributes
        if (series.length > 0 && attributes.every(attr => attr && attr.attribute)) {
          validCharts.push({
            chartOpts: appBuildGenericDailyGraph(series, attributes, this.labels),
            chartsAttributes: attributes,
          });
        }
      }

      return validCharts;
    }),
  );

  readonly attributesConfig$ = combineLatest([
    this._deviceId$,
    this.cachedDeviceConfig$,
    this.powerData$,
  ]).pipe(
    switchMap(([deviceId, cachedConfig]) => {
      // If we already have cached config, use it immediately
      // Otherwise, getAttributesConfig will try to get from telemetry
      return this.getAttributesConfig(deviceId);
    }),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    shareReplay(1),
  );

  readonly nameSelectionFn = (item: any) => item.name;
  readonly compareFn = (a: any, b: any) => a.id === b.id;
  readonly isAttributeOptDisabled = (attributes: ChartAttributes[], attribute: ChartAttributes) => {
    const { attribute: { graphOpts: { unit } } } = attribute;
    const selectedUnits = attributes.map(({ attribute: { graphOpts: { unit } } }) => unit);
    const uniqueSelectedUnits = [...new Set(selectedUnits)];

    if (uniqueSelectedUnits.includes(unit)) {
      return false;
    }

    return uniqueSelectedUnits.length < 2 ? false : true;
  };

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
  ) {
    this.getAttributesConfig = this.getAttributesConfig.bind(this);
    this.addChart = this.addChart.bind(this);
    this.updateChart = this.updateChart.bind(this);
    this.deleteChart = this.deleteChart.bind(this);
    this.onChartInit = this.onChartInit.bind(this);
  }

  buildPowerGraph(
    tz: string,
    selectedSource: NodeType[],
    sourceOptions: NodeType[],
    date: string,
    powers: Partial<Inverter>[],
  ) {
    const powerStateSeries = adaptPowersToGraph(powers, date, sourceOptions, tz);

    const chartAttributes: ChartAttributes[] = chartDeviceAttributes.map((attribute) => {
      return ({
        id: attribute.id,
        attribute: attribute,
        index: 0,
      })
    });

    const dailyPowerState = appBuildGenericDailyGraph(powerStateSeries, chartAttributes, this.labels);
    const hasValues = powerStateSeries.some(({ values }) => values.map(([_, value]) => value).some((value) => value !== 0));

    let graph = updateSelectedAndAxisLimits(dailyPowerState, {
      P_DC_TOT: selectedSource.includes('solar'),
      P_EXP: selectedSource.includes('grid'),
      P_LOAD: selectedSource.includes('home'),
      P_B_AGGR: selectedSource.includes('battery'),
      BP: selectedSource.includes('battery'),
      BP1: selectedSource.includes('battery'),
      BP2: selectedSource.includes('battery'),
    });

    if (!this.zoomed) {
      return {
        graph: { ...graph, dataZoom: [], grid: { ...(graph.grid || {}), bottom: 10 } },
        hasValues,
      };
    }

    // Re-apply saved dataZoom UI state so [options] notMerge doesn't reset it
    if (this.dataZoomState && Array.isArray(graph.dataZoom)) {
      return {
        graph: {
          ...graph,
          dataZoom: graph.dataZoom.map((dz) => ({
            ...dz,
            start: this.dataZoomState!.start,
            end: this.dataZoomState!.end,
          })),
        },
        hasValues,
      };
    }

    return { graph, hasValues };
  }

  toggleZoom() {
    this.zoomed = !this.zoomed;
    if (this.zoomed) {
      // Disconnect charts — echarts.connect propagates dataZoom causing feedback loop
      this.chartInstances.forEach((ec) => (ec as any).group = undefined);
    } else {
      this.dataZoomState = null;
      // Reconnect charts for tooltip sync
      echarts.connect(this.chartInstances as any);
    }
    // Force powerData$ to re-emit by re-selecting current sources
    this.updateSourcesSelection();
  }

  updateSourcesSelection(selectedItems: NodeType[] = this.sourceSelection$.value) {
    if (!selectedItems.length) {
      return;
    }
    this.sourceSelection$.next(selectedItems);
  }

  async getAttributesConfig(deviceId?: string) {
    if (!deviceId) {
      return;
    }

    const product = new ProductModelService(deviceId);
    const type = product.type?.name === 'inverter' ? product.type?.value : undefined;
    const phase = product.type?.name === 'inverter' ? product.type.phase : undefined;
    if (!type || !phase) {
      return;
    }

    // Check if we have cached device config
    let n_mppt: number;
    let n_strings: number;

    const cachedConfig = this.cachedDeviceConfig$.value;
    if (cachedConfig) {
      // Use cached values
      n_mppt = cachedConfig.n_mppt;
      n_strings = cachedConfig.n_strings;
    } else {
      // Try to get from telemetry
      const powerData = await this.powerData$.pipe(first()).toPromise();
      const { powers } = powerData;
      const sample = powers.length && powers[powers.length - 1]
      if (!sample) {
        return;
      }

      n_mppt = Number(sample.N_MPPT);
      n_strings = Number(sample.N_MAX_STRINGS);
      if (!n_mppt) {
        return;
      }

      // Cache the values for future use
      this.cachedDeviceConfig$.next({ n_mppt, n_strings });
    }

    const showStrings = ['C', 'D'].includes(product.inverterProductType as string);

    const availableAttributes = deviceAttributes
      .filter((s) => s.categoryId !== 'strings' || (showStrings && n_strings))
      .filter((s) => s.scope.includes(type) || s.scope.length === 0)
      .filter((s) => s.phase.includes(phase) || s.phase.length === 0);

    const attributesByCategory = availableAttributes.reduce((acc, props) => {
      if (!acc[props.categoryId]) {
        acc[props.categoryId] = [];
      }
      acc[props.categoryId].push(props);
      return acc;
    }, {} as Record<string, DeviceAttributes[]>);

    return Object.entries(attributesByCategory).map(([categoryId, attributes]) => {
      return {
        categoryId: categoryId as DeviceCategoryId,
        attributes: attributes
          .filter((attribute) => {
            if (attribute.id.includes('MP')) {
              return Number(attribute.id.replace('MP', '')) <= n_mppt;
            }

            if (attribute.id.includes('MC')) {
              return Number(attribute.id.replace('MC', '')) <= n_mppt;
            }

            if (attribute.id.includes('MT')) {
              return Number(attribute.id.replace('MT', '')) <= n_mppt;
            }

            if (attribute.id.includes('SC')) {
              return Number(attribute.id.replace('SC', '')) <= n_strings;
            }

            return true;
          })
          .map((attribute) => ({
            id: attribute.id,
            index: 0,
            attribute,
          })).flat(),
      };
    });
  }

  addChart(attributes: ChartAttributes[]) {
    const currentCharts = this.chartsAttributes$.value;
    this.chartsAttributes$.next([...currentCharts, attributes]);
  }

  updateChart(index: number, attributes: ChartAttributes[]) {
    const currentCharts = this.chartsAttributes$.value;
    const currentChart = currentCharts[index];
    const areEqual = isEqual(currentChart, attributes);
    if (areEqual) {
      return;
    }

    currentCharts[index] = attributes;
    this.chartsAttributes$.next([...currentCharts]);
  }

  deleteChart(index: number) {
    const currentCharts = this.chartsAttributes$.value;
    currentCharts.splice(index, 1);
    this.chartsAttributes$.next([...currentCharts]);
  }

  onChartInit(ec: ECharts) {
    this.chartInstances.push(ec);

    // Connect charts for tooltip sync when not zoomed.
    // Disconnected when zoomed because echarts.connect propagates dataZoom,
    // causing a feedback loop that resets slider positions.
    if (!this.zoomed) {
      echarts.connect(this.chartInstances as any);
    }

    // Preserve dataZoom UI state across reactive option resets
    ec.on('datazoom', (params: any) => {
      const state = params.batch?.[0] || params;
      if (state.start !== undefined && state.end !== undefined) {
        this.dataZoomState = { start: state.start, end: state.end };
      }
    });
  }

  onExport(series: any) {
    const timeTransformFn = (valuex: number) => {
      const hour = Math.floor((valuex + (12 * 60 * 60)) / ( 60 * 60));
      const minutes = Math.floor(((valuex + (12 * 60 * 60)) / 60) % 60);
      return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    const dataTransformFn = (name: string, value: any) => {
      const attributes = deviceAttributes.concat(chartDeviceAttributes)
      const unit = attributes.find((attr) => attr.id === name)?.graphOpts.unit || 'kW';
      const _value = attributes.find((attr) => attr.id === name)?.graphOpts.transformUnitFn?.(value) || value;

      return {
        name,
        value: `${_value} ${unit}`,
      };
    }

    const date = this.dateSelection$.value;
    const deviceId = this._deviceId$.value;
    const filename = `${deviceId}_${date}.xlsx`;

    exportToExcel(series, this.labels, timeTransformFn, filename, date, dataTransformFn);
  }
}
